/* eslint-disable @typescript-eslint/no-unused-vars */
import * as Yup from "yup";
import React, { useEffect, useState } from "react";
import axios, { Method } from "axios";
import { AiFillCheckCircle } from "react-icons/ai";
import { IoMdArrowBack, IoMdArrowForward } from "react-icons/io";
import { Link } from "react-router-dom";
import { MdOutlineEmail, MdOutlinePhone } from "react-icons/md";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";

import ContainedButton from "components/layout/form/containedButton";
import CountdownTimer from "./CountdownTimer";
import OutlinedButton from "components/layout/form/outlinedButton";
import QuestionPool from "./QuestionPool";
import TextField from "components/layout/form/textField";
import TextSelect from "components/layout/form/TextSelect";
import ValidationContact from "./ValidationContact";
import api from "services/api";
import theme from "styles/theme";
import {
  registerValues,
  toggleFirstStep,
  toggleStep,
} from "store/actions/first-access-mi-actions";

import {
  Container,
  ContainerButtons,
  FormContainer,
  HeaderContainer,
  LinkNotEmail,
  ReceivedCode,
  SendAnother,
  SuccessContent,
  TokenInput,
  TokenIsValid,
} from "./styles";

const TokenAccess = (): JSX.Element => {
  const [emailTokenSent, setEmailTokenSent] = useState(false);
  const [phoneTokenSent, setPhoneTokenSent] = useState(false);
  const [emailTokenValidated, setEmailTokenValidated] = useState(false);
  const [phoneTokenValidated, setPhoneTokenValidated] = useState(false);
  const [sendOtherToken, setSendOtherToken] = useState(false);
  const [timeInSeconds, setTimeInSeconds] = useState(60);
  const [disableVerifyToken, setDisableVerifyToken] = useState(false);
  const [tokenGotRequested, setTokenGotRequested] = useState(false);
  const [tokenEmailRequested, setTokenEmailRequested] = useState(false);
  const [tokenPhoneRequested, setTokenPhoneRequested] = useState(false);
  const [tokenAllVerified, setTokenAllVerified] = useState(false);
  const [openModalQuestions, setOpenModalQuestions] = useState(false);
  const [openModalNewContact, setOpenModalNewContact] = useState(false);
  const [allQuestionsValid, setAllQuestionsValid] = useState(false);
  const [accessToken, setAccessToken] = useState("");

  const {
    firstStep,
    emails,
    phones,
    location,
    customerCode,
    document,
    siteId,
  } = useSelector(
    // @ts-ignore
    (state) => state.firstAccessReducer
  );
  const dispatch = useDispatch();

  const validationSchema = Yup.object().shape({
    email: Yup.string(),
    phone: Yup.string(),
    token: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      email: emails?.length > 0 ? emails?.[0].contactId : "",
      phone: phones?.length > 0 ? phones?.[0].contactId : "",
      token: "",
    },
    validationSchema,
    onSubmit: () => null,
  });

  const requestToken = async (event): Promise<void> => {
    event.persist();
    const AWAIT_TIME = 2500;

    setSendOtherToken(false);
    setTimeInSeconds(59);
    setTokenGotRequested(true);

    setTimeout(() => {
      setTokenGotRequested(false);
    }, AWAIT_TIME);

    let contactId = "";

    if (firstStep) {
      contactId = formik.values.email;
    } else {
      contactId = formik.values.phone;
    }

    await callTokenApi(contactId);
  };

  const callTokenApi = async (contactId): Promise<void> => {
    dispatch(registerValues({ contactId }));

    if (firstStep) {
      setTokenEmailRequested(false);
    } else {
      setTokenPhoneRequested(false);
    }

    toast.dismiss();

    const body = {
      tokenType: "REGISTER",
      url: "",
      contactId,
      customerCode,
      document,
    };

    try {
      await api.post(`${location}/tokens`, body);

      if (firstStep) {
        setEmailTokenSent(true);
      } else {
        setPhoneTokenSent(true);
      }
    } catch (err) {
      const erro = err.response;

      if (erro) {
        const { message } = err.response.data;

        if (err.response.status !== 401) {
          toast.error(message);
        }
      } else if (err.response.status !== 401) {
        toast.error("Ocorreu um erro interno na aplicação");
      }
    }
  };

  const setOtherToken = (): void => {
    setSendOtherToken(true);
    setTimeInSeconds(0);
  };

  const notEmailValidate = (): void => {
    dispatch(toggleFirstStep());
  };

  const notPhoneValidate = (): void => {
    setOpenModalQuestions(true);
  };

  const verifyToken = async (): Promise<void> => {
    setDisableVerifyToken(true);

    const headers = {
      access_token: accessToken,
    };

    const userId = location.split("/").pop();

    const { token } = formik.values;

    const body = {
      token,
    };

    try {
      const response = await api.post(
        `industrial/customer/v1/users/${userId}/validations`,
        body,
        {
          headers,
        }
      );

      if (response.status === 202) {
        setTokenAllVerified(true);

        if (firstStep) {
          setEmailTokenValidated(true);
        } else {
          setPhoneTokenValidated(true);
        }
      }
    } catch (error) {
      const message =
        error.response.data[0]?.message || error.response.data.message;

      toast.error(message);

      if (firstStep) {
        setTokenEmailRequested(true);
      } else {
        setTokenPhoneRequested(true);
      }
    }

    setDisableVerifyToken(false);
  };

  const nextStep = (): void => {
    dispatch(toggleStep(3));
  };

  const handleModal = (show, enableEditing): void => {
    setOpenModalQuestions(show);

    if (enableEditing) {
      setOpenModalNewContact(true);
    }
  };

  const setQuestionsAsValid = (): void => {
    setAllQuestionsValid(true);
  };

  useEffect(() => {
    (async () => {
      await generateAccessToken();
    })();
  }, []);

  useEffect(() => {
    if (emails?.length > 0) {
      formik.setFieldValue("email", emails?.[0]?.contactId || "");
    }
    if (phones?.length > 0) {
      formik.setFieldValue("phone", phones?.[0]?.contactId || "");
    }
  }, [emails, phones]);

  const generateAccessToken = async (): Promise<void> => {
    const req = axios.create({
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Accept-Language": "pt-BR",
        client_id: process.env.REACT_APP_CLIENT_ID,
      },
      baseURL: process.env.REACT_APP_API_URL_OAUTH,
    });

    const header = {
      "Content-Type": "application/json",
      "Accept-Language": "pt-BR",
      Authorization: process.env.REACT_APP_SIGNIN_AUTHORIZATION,
    };
    const parameters = {
      method: "POST" as Method,
      headers: header,
    };
    const body = {
      grant_type: "client_credentials",
    };

    try {
      const result = await req.post("oauth/access-token", body, parameters);

      setAccessToken(result.data.access_token);
    } catch (err) {
      const message =
        err.response.data[0]?.message || err.response.data.message;

      toast.error(message);
    }
  };

  const handleCloseModalNewContact = (): void => {
    setOpenModalNewContact(false);
  };

  const finishNewContact = (): void => {
    nextStep();
  };

  const userId = location.split("/").pop();

  return (
    <Container>
      <HeaderContainer>
        <h1>
          Localizamos seu cadastro.
          <br />
          Agora vamos te identificar por{" "}
          <strong>{firstStep ? "email" : "telefone"}</strong>
        </h1>
        <h2>
          Selecione uma das opcões dos {firstStep ? "emails" : "telefones"}{" "}
          abaixo para validação.
        </h2>
      </HeaderContainer>
      <FormContainer onSubmit={formik.handleSubmit}>
        {emails?.length > 0 && firstStep && !tokenAllVerified && (
          <>
            {!emailTokenSent && (
              <>
                <ReceivedCode>Email para validação</ReceivedCode>
                <TextSelect
                  name="email"
                  type="text"
                  placeholder="Selecione um email"
                  label="Selecione um email"
                  LeadingIcon={MdOutlineEmail}
                  propsLeadingIcon={{
                    color: theme.colors.blue,
                  }}
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  errorCondition={
                    !!formik.errors.email && !!formik.touched.email
                  }
                  errorMessage={`${formik.errors.email}`}
                  disabled={formik.isSubmitting}
                >
                  {emails?.map((item) => {
                    return (
                      <option key={item.contactId} value={item.contactId}>
                        {item.contact}
                      </option>
                    );
                  })}
                </TextSelect>
              </>
            )}
            <TokenInput>
              {emailTokenSent && (
                <>
                  <ReceivedCode>Código recebido</ReceivedCode>
                  <TextField
                    name="token"
                    type="text"
                    // disabled={disableEmailTokenInput}
                    label="Inserir código"
                    placeholder="Inserir código"
                    errorCondition={
                      !!formik.errors.token && !!formik.touched.token
                    }
                    errorMessage={`${formik.errors.token}`}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.token}
                  />

                  {!emailTokenValidated && (
                    <TokenIsValid>
                      {sendOtherToken ? (
                        <SendAnother
                          type="button"
                          disabled={tokenGotRequested}
                          onClick={(e) => requestToken(e)}
                        >
                          Não recebi o código, <span>Me envie novamente</span>
                        </SendAnother>
                      ) : (
                        <CountdownTimer
                          setOtherToken={setOtherToken}
                          timeInSeconds={timeInSeconds}
                        />
                      )}
                    </TokenIsValid>
                  )}

                  <OutlinedButton
                    width="100%"
                    block
                    disabled={disableVerifyToken}
                    onClick={(e) => verifyToken()}
                  >
                    Validar código
                  </OutlinedButton>
                </>
              )}
              {!emailTokenSent && !!formik.values.email && (
                <OutlinedButton
                  width="100%"
                  block
                  disabled={tokenGotRequested}
                  onClick={(e) => requestToken(e)}
                >
                  Enviar código de validação
                </OutlinedButton>
              )}
            </TokenInput>
          </>
        )}
        {emails?.length > 0 && firstStep && !tokenAllVerified && (
          <LinkNotEmail type="button" onClick={() => notEmailValidate()}>
            Não reconheço o email. Validar celular.
          </LinkNotEmail>
        )}
        {emails?.length === 0 && firstStep && !tokenAllVerified && (
          <LinkNotEmail type="button" onClick={() => notEmailValidate()}>
            Não existe email cadastrado. Validar celular.
          </LinkNotEmail>
        )}

        {phones?.length > 0 && !firstStep && !tokenAllVerified && (
          <>
            {!phoneTokenSent && (
              <>
                <ReceivedCode>Telefone para validação</ReceivedCode>
                <TextSelect
                  name="phone"
                  type="text"
                  placeholder="Selecione um telefone"
                  label="Selecione um telefone"
                  LeadingIcon={MdOutlinePhone}
                  propsLeadingIcon={{
                    color: theme.colors.blue,
                  }}
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  errorCondition={
                    !!formik.errors.phone && !!formik.touched.phone
                  }
                  errorMessage={`${formik.errors.phone}`}
                  disabled={formik.isSubmitting}
                >
                  {phones?.map((item) => {
                    return (
                      <option key={item.contactId} value={item.contactId}>
                        {item.contact}
                      </option>
                    );
                  })}
                </TextSelect>
              </>
            )}
            <TokenInput>
              {phoneTokenSent && (
                <>
                  <ReceivedCode>Código recebido</ReceivedCode>
                  <TextField
                    name="token"
                    type="text"
                    // disabled={disablePhoneTokenInput}
                    label="Inserir código"
                    placeholder="Inserir código"
                    errorCondition={
                      !!formik.errors.token && !!formik.touched.token
                    }
                    errorMessage={`${formik.errors.token}`}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.token}
                  />

                  {!phoneTokenValidated && (
                    <TokenIsValid>
                      {sendOtherToken ? (
                        <SendAnother
                          type="button"
                          disabled={tokenGotRequested}
                          onClick={(e) => requestToken(e)}
                        >
                          Não recebi o código, <span>Me envie novamente</span>
                        </SendAnother>
                      ) : (
                        <CountdownTimer
                          setOtherToken={setOtherToken}
                          timeInSeconds={timeInSeconds}
                        />
                      )}
                    </TokenIsValid>
                  )}

                  <OutlinedButton
                    width="100%"
                    block
                    disabled={disableVerifyToken}
                    onClick={(e) => verifyToken()}
                  >
                    Validar código
                  </OutlinedButton>
                </>
              )}
              {!phoneTokenSent && !!formik.values.phone && (
                <OutlinedButton
                  width="100%"
                  block
                  disabled={tokenGotRequested}
                  onClick={(e) => requestToken(e)}
                >
                  Enviar código de validação
                </OutlinedButton>
              )}
            </TokenInput>
          </>
        )}
        {phones?.length > 0 && !firstStep && !tokenAllVerified && (
          <LinkNotEmail type="button" onClick={() => notPhoneValidate()}>
            Não reconheço o telefone. Quero editar os dados cadastrados.
          </LinkNotEmail>
        )}
        {phones?.length === 0 && !firstStep && !tokenAllVerified && (
          <LinkNotEmail type="button" onClick={() => notPhoneValidate()}>
            Não existe telefone cadastrado. Quero editar os dados cadastrados.
          </LinkNotEmail>
        )}
        {!!tokenAllVerified && (
          <SuccessContent>
            <AiFillCheckCircle />
            <span>Token validado com sucesso</span>
          </SuccessContent>
        )}
        <ContainerButtons>
          <ContainedButton
            block
            type="button"
            continue
            loading={formik.isSubmitting}
            width="100%"
            TrailingIcon={IoMdArrowForward}
            propsTrailingIcon={{
              size: 20,
            }}
            disabled={!tokenAllVerified}
            onClick={nextStep}
          >
            Continuar
          </ContainedButton>
          <Link to="/">
            <OutlinedButton
              width="100%"
              LeadingIcon={IoMdArrowBack}
              propsLeadingIcon={{
                size: 20,
              }}
              onClick={() => dispatch(toggleStep(1))}
              color={theme.colors.purple}
            >
              Voltar
            </OutlinedButton>
          </Link>
        </ContainerButtons>
      </FormContainer>
      {openModalQuestions && (
        <QuestionPool
          body={{ document, userId, siteId, access_token: accessToken }}
          handleModal={handleModal}
          setLoading={() => console.log("setLoading")}
          errorStep={() => console.log("errorStep")}
          setQuestionsAsValid={setQuestionsAsValid}
        />
      )}
      <ValidationContact
        show={openModalNewContact}
        handleCloseModal={handleCloseModalNewContact}
        accessToken={accessToken}
        finishNewContact={finishNewContact}
      />
    </Container>
  );
};

export default TokenAccess;
